import { useEffect, useState } from "react";
import { FileDropzoneCard } from "../../../../components/common/file-dropzone-card";
import { FileUploadItem } from "../../../../components/common/file-upload-item";
import {
  ProductDatasheetUploadError,
  uploadProductDatasheet,
} from "../../utils/upload-product-datasheet";
import { DatasheetPreviewModal } from "./datasheet-preview-modal";

interface Props {
  /** GCS object key once an upload is complete. */
  objectKey: string | null;
  /** Original PDF file name shown to the user (and persisted alongside the key). */
  fileName: string | null;
  /** Called when an upload completes successfully. */
  onUploaded: (objectKey: string, fileName: string, fileSize?: number) => void;
  /** Called when the user removes the current datasheet. */
  onRemove: () => void;
  /** Optional scope segment used in the GCS object key (e.g. draft ID). */
  scope?: string;
  disabled?: boolean;
}

export function DatasheetUpload({
  objectKey,
  fileName,
  onUploaded,
  onRemove,
  scope,
  disabled = false,
}: Props) {
  const [progress, setProgress] = useState(0);
  const [uploadingName, setUploadingName] = useState<string | null>(null);
  const [uploadingSize, setUploadingSize] = useState<number | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [lastFile, setLastFile] = useState<File | null>(null);
  const [uploadedSize, setUploadedSize] = useState<number | undefined>(undefined);
  // Local blob URL for previewing the just-uploaded PDF without a BE round-trip.
  // Only available in the session that did the upload — after a draft reload
  // there's no File object, so the preview button is hidden.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (!lastFile) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(lastFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [lastFile]);

  const runUpload = async (file: File) => {
    setUploadingName(file.name);
    setUploadingSize(file.size);
    setProgress(0);
    setError(null);
    setLastFile(file);
    try {
      const { objectKey: key, fileName: name } = await uploadProductDatasheet(file, {
        scope,
        onProgress: (p) => setProgress(p / 100),
      });
      setUploadedSize(file.size);
      setUploadingName(null);
      onUploaded(key, name, file.size);
    } catch (err) {
      const message =
        err instanceof ProductDatasheetUploadError
          ? err.message
          : "Upload failed. Please try again.";
      setError(message);
    }
  };

  if (error && lastFile) {
    return (
      <FileUploadItem
        title="Datasheet PDF"
        fileName={lastFile.name}
        fileSize={lastFile.size}
        status="failed"
        errorMessage={error}
        onRetry={() => runUpload(lastFile)}
        onRemove={() => {
          setError(null);
          setLastFile(null);
          setUploadingName(null);
        }}
      />
    );
  }

  if (uploadingName) {
    return (
      <FileUploadItem
        title="Datasheet PDF"
        fileName={uploadingName}
        fileSize={uploadingSize}
        progress={progress}
        status="uploading"
      />
    );
  }

  if (objectKey && fileName) {
    return (
      <>
        <FileUploadItem
          title="Datasheet PDF"
          fileName={fileName}
          fileSize={uploadedSize}
          status="uploaded"
          onPreview={previewUrl ? () => setPreviewOpen(true) : undefined}
          onRemove={
            disabled
              ? undefined
              : () => {
                  setUploadedSize(undefined);
                  setLastFile(null);
                  onRemove();
                }
          }
        />
        {previewOpen && previewUrl ? (
          <DatasheetPreviewModal
            url={previewUrl}
            fileName={fileName}
            onClose={() => setPreviewOpen(false)}
          />
        ) : null}
      </>
    );
  }

  return (
    <FileDropzoneCard
      title="Upload product datasheet"
      supportedFormats="PDF only - max 25MB"
      helperText="Used in the catalog preview and fed into the AI assistant for this product."
      accept="application/pdf"
      disabled={disabled}
      onFileSelect={runUpload}
    />
  );
}
